/**
 * Profile Menu - User dropdown with auth actions
 */

import { useState, useRef, useEffect } from 'react';
import { useAuthStore, useSubscriptionStore, selectIsPro } from '../stores';
import { useUIStore } from '../stores';
import './ProfileMenu.css';

interface ProfileMenuProps {
  onSignIn: () => void;
}

export function ProfileMenu({ onSignIn }: ProfileMenuProps) {
  const { user, isAuthenticated, isAnonymous, signOut, claimProfile } = useAuthStore();
  const isPro = useSubscriptionStore(selectIsPro);
  const navigate = useUIStore((s) => s.navigate);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  if (!isAuthenticated) {
    return (
      <button className="profile-btn profile-btn--signin" onClick={onSignIn}>
        Sign In
      </button>
    );
  }
  
  const displayName = user?.displayName || (isAnonymous ? 'Guest' : 'User');
  const photoURL = user?.photoURL;
  const initial = displayName.charAt(0).toUpperCase();
  
  return (
    <div className="profile-menu" ref={menuRef}>
      <button 
        className="profile-btn" 
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {photoURL ? (
          <img src={photoURL} alt={displayName} className="profile-avatar" />
        ) : (
          <span className="profile-avatar profile-avatar--initial">{initial}</span>
        )}
      </button>
      
      {isOpen && (
        <div className="profile-dropdown">
          <div className="profile-dropdown__header">
            <span className="profile-dropdown__name">{displayName}</span>
            {isPro && (
              <span className="profile-dropdown__badge profile-dropdown__badge--pro">PRO</span>
            )}
            {isAnonymous && (
              <span className="profile-dropdown__badge">Guest</span>
            )}
          </div>
          
          {user?.email && (
            <span className="profile-dropdown__email">{user.email}</span>
          )}
          
          <div className="profile-dropdown__divider" />
          
          {isAnonymous && (
            <button 
              className="profile-dropdown__item profile-dropdown__item--accent"
              onClick={() => {
                claimProfile();
                setIsOpen(false);
              }}
            >
              🔗 Claim Profile
            </button>
          )}
          
          {!isPro && !isAnonymous && (
            <button
              className="profile-dropdown__item profile-dropdown__item--accent"
              onClick={() => {
                navigate('pro');
                setIsOpen(false);
              }}
            >
              ✨ Upgrade to Pro
            </button>
          )}

          {isPro && (
            <button
              className="profile-dropdown__item"
              onClick={() => {
                navigate('analytics');
                setIsOpen(false);
              }}
            >
              📈 Analytics
            </button>
          )}

          {isPro && (
            <button
              className="profile-dropdown__item"
              onClick={() => {
                navigate('report');
                setIsOpen(false);
              }}
            >
              📊 Skill Report
            </button>
          )}

          {isPro && (
            <button
              className="profile-dropdown__item"
              onClick={() => {
                navigate('pro');
                setIsOpen(false);
              }}
            >
              💎 Manage Subscription
            </button>
          )}

          <button
            className="profile-dropdown__item"
            onClick={() => setIsOpen(false)}
          >
            ⚙️ Settings
          </button>

          <div className="profile-dropdown__divider" />
          
          <button 
            className="profile-dropdown__item profile-dropdown__item--danger"
            onClick={() => {
              signOut();
              setIsOpen(false);
            }}
          >
            🚪 Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
